<script>
    import { UserStore } from '../stores/UserStore.svelte.js';
    import * as Icons from 'lucide-svelte';
    import LySection from '../layouts/components/LySection.svelte';
    import LyContent from '../layouts/components/LyContent.svelte';
    import LyHeadline from '../layouts/components/LyHeadline.svelte';
    import LyP from '../layouts/components/LyP.svelte';
    import LyHeader from '../layouts/components/LyHeader.svelte';
    import LyButton from '../layouts/forms/LyButton.svelte';
    import LyListTwoCols from '../layouts/lists/LyListTwoCols.svelte';
    import LyListKey from '../layouts/lists/LyListKey.svelte';
    import LyListValue from '../layouts/lists/LyListValue.svelte';
    import LyButtonLine from '../layouts/forms/LyButtonLine.svelte';
    import LyColor from '../layouts/components/LyColor.svelte';
    import LyGroup from '../layouts/users/LyGroup.svelte';
    import LyUser from '../layouts/users/LyUser.svelte';
    import LyInputText from '../layouts/forms/LyInputText.svelte';
    import { ApiService } from '../services/ApiService.js';

    const api = new ApiService();
    let thisUserId = $derived(UserStore.userId || 0);

    // -
    let doEditNickname = $derived(false);
    let valueNickname = $derived(UserStore.nickName || '');
    /** @type {ReturnType<typeof setTimeout>|null} */
    let nicknameTimer = null;

    function clearNicknameTimer() {
        if (nicknameTimer === null) {
            return;
        }
        clearTimeout(nicknameTimer);
        nicknameTimer = null;
    }

    function cancelNickname() {
        clearNicknameTimer();
        doEditNickname = false;
        valueNickname = UserStore.nickName || '';
        UserStore.setPauseWhoAmI(false);
    }

    function editNickname() {
        clearNicknameTimer();
        doEditNickname = true;
        valueNickname = UserStore.nickName || '';
        UserStore.setPauseWhoAmI(true);
        nicknameTimer = setTimeout(() => {
            cancelNickname();
        }, 10000);
    }

    async function saveNickname() {
        clearNicknameTimer();
        doEditNickname = false;
        await api.patchUser(thisUserId, {
            nickname: valueNickname,
        });
        UserStore.setPauseWhoAmI(false);
    }

    // -
    let doEditFullName = $derived(false);
    let valueFirstname = $derived(UserStore.firstName || '');
    let valueLastname = $derived(UserStore.lastName || '');
    /** @type {ReturnType<typeof setTimeout>|null} */
    let fullNameTimer = null;

    function clearFullNameTimer() {
        if (fullNameTimer === null) {
            return;
        }
        clearTimeout(fullNameTimer);
        fullNameTimer = null;
    }

    function cancelFullName() {
        clearFullNameTimer();
        doEditFullName = false;
        valueFirstname = UserStore.firstName || '';
        valueLastname = UserStore.lastName || '';
        UserStore.setPauseWhoAmI(false);
    }

    function editFullName() {
        clearFullNameTimer();
        doEditFullName = true;
        valueFirstname = UserStore.firstName || '';
        valueLastname = UserStore.lastName || '';
        UserStore.setPauseWhoAmI(true);
        fullNameTimer = setTimeout(() => {
            cancelFullName();
        }, 10000);
    }

    async function saveFullName() {
        clearFullNameTimer();
        doEditFullName = false;
        await api.patchUser(thisUserId, {
            firstname: valueFirstname,
            lastname: valueLastname,
        });
        UserStore.setPauseWhoAmI(false);
    }
</script>

<LyContent wrapper={true}>
    <LyHeader>
        <LyHeadline icon={Icons.User}>Benutzerkonto</LyHeadline>
    </LyHeader>

    <LySection tag="div">
        <LyP>
            Hier ist dein persönlicher Bereich, wo du alles rund um
            <strong>Dein Benutzerkonto</strong> finden kannst.
        </LyP>
    </LySection>
</LyContent>

<LyButtonLine tag="section" mt={true}>
    <LyButton
        label="Abmelden"
        color={true}
        disabled={UserStore.waiting}
        onclick={() => UserStore.doLogout()}
        ask="Wirklich abmelden?" />
</LyButtonLine>

<LyContent wrapper={true} mt={true}>
    <LyHeader>
        <LyHeadline weight={2}>Benutzerinformationen</LyHeadline>
        <LyP>Deine persönlichen Daten und Account-Informationen.</LyP>
    </LyHeader>

    <LyListTwoCols mt={true}>
        <LyListKey inx={1}>ID</LyListKey>
        <LyListValue inx={1}><span class="font-mono">#{thisUserId}</span></LyListValue>

        <LyListKey inx={2}>Benutzername</LyListKey>
        <LyListValue inx={2}><strong class="font-mono">{UserStore.userName || '-'}</strong></LyListValue>

        <LyListKey inx={3}>Kürzel</LyListKey>
        <LyListValue inx={3}>
            <LyUser user_id={thisUserId} />
        </LyListValue>

        <LyListKey inx={4}>Name</LyListKey>
        <LyListValue inx={4}>
            {#if doEditFullName}
                <div class="flex items-center gap-2">
                    <span>
                        <LyInputText
                            name="firstname"
                            autocomplete={true}
                            autocorrect={true}
                            label="Vorname"
                            placeholder=""
                            bind:value={valueFirstname}
                            onkeydown={(/** @type {KeyboardEvent} */ e) => e.key === 'Enter' && saveFullName()} />
                    </span>
                    <span>
                        <LyInputText
                            name="lastname"
                            label="Nachname"
                            placeholder=""
                            autocomplete={true}
                            autocorrect={true}
                            bind:value={valueLastname}
                            onkeydown={(/** @type {KeyboardEvent} */ e) => e.key === 'Enter' && saveFullName()} />
                    </span>
                    <span>
                        <Icons.SquareCheck
                            size="1.5em"
                            class="text-success-900 hover:text-success-500 cursor-pointer"
                            onclick={saveFullName} />
                    </span>
                    <span>
                        <Icons.SquareX
                            size="1em"
                            class="text-error-900 hover:text-error-500 cursor-pointer"
                            onclick={cancelFullName} />
                    </span>
                </div>
            {:else}
                <div class="flex items-center gap-2">
                    <span>
                        <Icons.SquarePen
                            size="1em"
                            class="text-info-800 hover:text-info-500 cursor-pointer"
                            onclick={editFullName} />
                    </span>
                    <span>
                        {valueFirstname === '' ? '-' : valueFirstname}
                        <strong>{valueLastname === '' ? '-' : valueLastname}</strong>
                    </span>
                </div>
            {/if}
        </LyListValue>

        <LyListKey inx={5}>Spitzname</LyListKey>
        <LyListValue inx={5}>
            {#if doEditNickname}
                <div class="flex items-center gap-2">
                    <span>
                        <LyInputText
                            name="nickname"
                            autocomplete={true}
                            autocorrect={true}
                            bind:value={valueNickname}
                            onkeydown={(/** @type {KeyboardEvent} */ e) => e.key === 'Enter' && saveNickname()} />
                    </span>
                    <span>
                        <Icons.SquareCheck
                            size="1.5em"
                            class="text-success-900 hover:text-success-500 cursor-pointer"
                            onclick={saveNickname} />
                    </span>
                    <span>
                        <Icons.SquareX
                            size="1em"
                            class="text-error-900 hover:text-error-500 cursor-pointer"
                            onclick={cancelNickname} />
                    </span>
                </div>
            {:else}
                <div class="flex items-center gap-2">
                    <span>
                        <Icons.SquarePen
                            size="1em"
                            class="text-info-800 hover:text-info-500 cursor-pointer"
                            onclick={editNickname} />
                    </span>
                    <span>
                        {valueNickname === '' ? '-' : valueNickname}
                    </span>
                </div>
            {/if}
        </LyListValue>

        <LyListKey inx={6}>E-Mail</LyListKey>
        <LyListValue inx={6}>{UserStore.email || '-'}</LyListValue>

        <LyListKey inx={7}>Gruppen</LyListKey>
        {#if UserStore.groups?.length}
            {#each UserStore.groups as group}
                <LyListValue inx={7}>
                    <LyGroup {group} />
                </LyListValue>
            {/each}
        {:else}
            <LyListValue inx={7}>-</LyListValue>
        {/if}

        <LyListKey inx={8}>Farbe</LyListKey>
        <LyListValue inx={8}>
            <LyColor color={UserStore.color || '#000000'} label={UserStore.color || '-'} mono={true} />
        </LyListValue>
    </LyListTwoCols>
</LyContent>
